import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form-mui';
import { useLocation, useNavigate } from 'react-router-dom';
import { APIContext } from '../Contexts/APIContext';
import { SessionContext } from '../Contexts/SessionContext';
import { TelemetryContext } from '../Contexts/TelemetryContext';
import { SurveyViewModel } from '../Contexts/SessionState';
import DeselectionReasonDialog from '../TripRate/Filters/Summary/DeselectionReasonDialog';
import ExportSitePDFDialog from "./ExportSitePDFDialog";
import { getLegacySiteReturnLabel, getSiblingSiteUrl, isInternalReturnTo } from './SiteNavigation';
import DesignFeatures from './SitePages/DesignFeatures';
import DevelopmentUnits from './SitePages/DevelopmentUnits/DevelopmentUnits';
import LocationDetails from './SitePages/LocationDetails';
import Parking from './SitePages/Parking';
import PublicTransport from './SitePages/PublicTransport';
import SitePageOptions from './SitePages/SitePageOptions';
import SurvayDaysContainer from './SitePages/SurveyDays/SurveyDays';
import TravelPlanContainer from './SitePages/TravelPlan/TravelPlanContainer';
import TravelPlanMonitoringReportContainer from './SitePages/TravelPlan/TravelPlanMonitoringReportContainer';
import SiteViewModel from './ViewModels/SiteViewModel';
import ExportSitePDFViewModel from './ViewModels/ExportSitePDFViewModel';
import TripRateParametersDialog from './SitePages/TripRateParametersDialog';
import { AuthContext } from '../Authorisation/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { SiteEditContext } from './SiteEditContext';

const steps = [LocationDetails, PublicTransport, DesignFeatures,
    DevelopmentUnits, Parking, SurvayDaysContainer,
    TravelPlanContainer, TravelPlanMonitoringReportContainer];

export default function Site() {
    const navigate = useNavigate();
    const { sessionState, setSessionField } = useContext(SessionContext);
    const { post, get, showMessage, postPDF, loading } = useContext(APIContext);
    const { checkRole } = useContext(AuthContext);
    const { logSiteDetailsViewed } = useContext(TelemetryContext);
    const [stepIndex, setStepIndex] = useState(0);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const siteRef = params.get('siteRef') || '';
    const returnTab = params.get('fromTab') || '';
    const origin = params.get('origin') || '';
    const returnTo = params.get('returnTo') || '';
    const returnLabel = params.get('returnLabel') || '';
    const type = params.get('type') || '';
    const typeRef = useRef('');
    const lastLoggedSiteRef = useRef<string | null>(null);
    const [availableTripRateParameters, setAvailaleTripRateParameters] = useState<string[]>([])
    const [paramDialogOpen, setParamDialogOpen] = useState(false);
    const [nextRef, setNextRef] = useState('');
    const [prevRef, setPrevRef] = useState('');
    const [exportPDFDialog, setExportPDFDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [addDevelopmentOpen, setAddDevelopmentOpen] = useState(false);
    const [newDevelopmentName, setNewDevelopmentName] = useState('');
    const siteEditContext = useMemo(
        () => ({ isEditing, setIsEditing }),
        [isEditing],
    );

    const [open, setOpen] = useState<string | null>(null);

    const formContext = useForm<SiteViewModel>({
        defaultValues: new SiteViewModel(),
    });

    const { reset, setValue, watch } = formContext;

    const nextSurvey = watch('nextReSurveyReference');
    const lastSurvey = watch('previousReSurveyReference');

    const [selectedMode, setSelectedMode] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');

    const handleSurveySelection = (mode: number | null, date: string) => {
        setSelectedMode(mode);
        setSelectedDate(date);
    };

    useEffect(() => {
        if (siteRef != null) {
            getSite();
            var mainLandUse: string = type === 'siteList' ? sessionState.siteListMain : sessionState.tripRateMain;
            var subLandUse: string = type === 'siteList' ? sessionState.siteListSub : sessionState.tripRateSub;
            post('Site', `GetNextAndPrevSite?reference=${siteRef}&mainLandUse=${mainLandUse}&subLandUse=${subLandUse}&orderBy=${sessionState.siteListSort}&order=${sessionState.siteListDir}&search=${sessionState.siteListTextSearch}&landUseSearch=${sessionState.siteListLandSearch}`, sessionState.selectedSitesAndSurveys.map(m => m.siteRef), (data) => {
                setPrevRef(data[0]);
                setNextRef(data[1]);
            });
        }
    }, [siteRef]);

    useEffect(() => {
        typeRef.current = type;
    }, [type])

    const getSite = async () => {
        get('Site', `GetSite?reference=${siteRef}`, (data) => {
            reset(data);

            if (siteRef && lastLoggedSiteRef.current !== siteRef) {
                logSiteDetailsViewed(siteRef);
                lastLoggedSiteRef.current = siteRef;
            }
        });
        get('Site', `GetAvailableParametersForSite?reference=${siteRef}`, setAvailaleTripRateParameters);
    }

    const CurrentStepComponent = steps[stepIndex];

    const ref = watch('reference');
    const survType = watch('surveyType');
    const area = watch('area');
    const reigion = watch('region');
    const hasParkingDetails = watch('hasParkingDetails');
    const developmentDetails = watch('hasDevelopmentDetails');
    const hasTravelPlan = watch('hasTravelPlan');
    const isDftConnectivityToolEnabled = watch('isDftConnectivityToolEnabled');
    const isAdmin = checkRole(['Admin', 'SuperAdmin']);
    const parkingUnsupportedLandUses = ['12/A', '12/C', '13/A', '13/B', '13/C'];
    const parkingSupported = !parkingUnsupportedLandUses.includes(siteRef.split('-').slice(1, 3).join('/'));

    const oncheckboxchange = (e: any) => {
        e.stopPropagation();
        if (checked && setOpen) {
            setOpen(!checked ? null : siteRef);
            return;
        }

        if (typeRef.current === 'siteFiltering') {

            var sites = sessionState.landusePage.sites;

            var siteExists = sites.find((site) => site === siteRef);

            if (siteExists) {
                const index = sites.indexOf(siteRef);
                if (index > -1) {
                    sites.splice(index, 1);
                }
            }
            else {
                sites.push(siteRef);
            }

            const otherSiteSurvey = sites.find((site) => (site === nextSurvey) || (site === lastSurvey));

            if (otherSiteSurvey) {
                const firstSiteRef = otherSiteSurvey < siteRef ? otherSiteSurvey : siteRef;
                const secondSiteRef = otherSiteSurvey < siteRef ? siteRef : otherSiteSurvey;
                showMessage("error", "Site: " + firstSiteRef + " has been re-surveyed by Site: " + secondSiteRef + " only one site can be selected");
                return;
            }

            setSessionField('landusePage.sites', sites);
            return;
        }

        const selectedSites = sessionState.selectedSitesAndSurveys;
        const currentSite = selectedSites.find((site) => (site.surveyRef === siteRef))
        const otherSiteSurvey = selectedSites.find((site) => (site.resurveyRef === siteRef && site.siteActive) || (currentSite?.resurveyRef === site.surveyRef && site.siteActive));

        if (otherSiteSurvey) {
            const firstSiteRef = otherSiteSurvey.surveyRef < siteRef ? otherSiteSurvey.surveyRef : siteRef;
            const secondSiteRef = otherSiteSurvey.surveyRef < siteRef ? siteRef : otherSiteSurvey.surveyRef;
            showMessage("error", "Site: " + firstSiteRef + " has been re-surveyed by Site: " + secondSiteRef + " only one site can be selected");
            return;
        }

        let updatedSites: SurveyViewModel[];
        updatedSites = selectedSites.map((site) =>
            site.surveyRef === siteRef
                ? { ...site, reasonForSurveyRemoval: "", reasonForSiteRemoval: '', siteActive: !site.siteActive }
                : site
        );

        setSessionField('selectedSitesAndSurveys', updatedSites);
    }

    const reason = sessionState.selectedSitesAndSurveys.find((site: any) => site.surveyRef === siteRef)?.reasonForSiteRemoval;
    const checked = typeRef.current === "siteFiltering" ?
        sessionState.landusePage.isAllSelected
            ? sessionState.landusePage.sites.find((x) => x === siteRef) === undefined
            : sessionState.landusePage.sites.find((x) => x === siteRef) !== undefined
        :
        sessionState.selectedSitesAndSurveys.find((site: any) => site.siteRef === siteRef)?.siteActive;

    const backOptions = () => {
        if (returnTo && isInternalReturnTo(returnTo)) {
            navigate(returnTo);
            return;
        }

        const type = typeRef.current;
        if (type === 'siteList') {
            navigate('/sitelist');
        } else if (type === 'summaryFiltering') {
            const originParam = origin ? `&origin=${origin}` : '';
            navigate(`/triprate/summary?toTab=${returnTab}${originParam}`);
        } else if (type === 'rankorder') {
            navigate(`/triprate/rankorder?toTab=${returnTab}&selectionType=site`);
        } else {
            navigate('/triprate/filter');
        }
    };

    const backButtonText = returnLabel || getLegacySiteReturnLabel(type, returnTab, origin);

    const openPDFDialog = () => setExportPDFDialog(true);
    const closePDFDialog = () => setExportPDFDialog(false);
    const openParamDialog = () => setParamDialogOpen(true);
    const closeParamDialog = () => setParamDialogOpen(false);


    const runTripRateForSurvey = (selectedParam?: number) => {
        const base = `/triprate/results?selectedSurvey=${siteRef}&selectedMode=${selectedMode ?? 0}&surveyDate=${(selectedDate || '').split('/').join(`-`)}&fromTab=siteSummary`;
        if (selectedParam != null) {
            navigate(`${base}&selectedParam=${selectedParam}`);
            return;
        }
        navigate(base);
    };

    const getReportPDF = async (exportViewModel: ExportSitePDFViewModel) => {
        exportViewModel.reference = siteRef;
        postPDF("PDFGenerator", "GetSiteDetailsPDF", "Site Export", false, exportViewModel);
    }

    const addDevelopment = () => {
        setNewDevelopmentName('');
        setAddDevelopmentOpen(true);
    };

    const confirmAddDevelopment = () => {
        const tradeName = newDevelopmentName.trim();
        if (!tradeName || loading) return;

        get(
            'DevelopmentDetails',
            `AddDevelopment?reference=${siteRef}&tradeName=${encodeURIComponent(tradeName)}`,
            () => {
                setAddDevelopmentOpen(false);
                showMessage('success', 'Development added successfully');
                setValue('hasDevelopmentDetails', true);
            },
        );
    };

    const addParking = () => {
        if (loading) return;

        post('ParkingDetails', `CreateParking?reference=${siteRef}`, {}, () => {
            showMessage('success', 'Parking details added successfully');
            setValue('hasParkingDetails', true);
        });
    };

    const addTravelPlan = () => {
        if (loading) return;

        post('TravelPlan', `CreateTravelPlanV2?reference=${siteRef}`, {}, () => {
            showMessage('success', 'Travel Plan V2 added successfully');
            setValue('hasTravelPlan', true);
        });
    };

    return (
        <SiteEditContext.Provider value={siteEditContext}>
        <Box sx={{ height: '100%', flexDirection: "column", display: "flex" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40 }}>
                <Grid container alignItems="center" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Grid>
                    <Button startIcon={<ArrowCircleLeftIcon />} onClick={backOptions} variant="outlined">{backButtonText}</Button>
                </Grid>
                <Grid>
                    <Typography><b>Ref Number</b> - {ref}</Typography>
                </Grid>
                <Grid>
                    <Typography><b>Type</b> - {survType}</Typography>
                </Grid>
                <Grid>
                    <Typography><b>Area</b> - {area}</Typography>
                </Grid>
                <Grid>
                    <Typography><b>Region</b> - {reigion}</Typography>
                </Grid>
                {typeRef.current !== 'siteList' &&
                    <Grid>
                        <FormControlLabel control={<Checkbox onChange={oncheckboxchange} checked={checked} />} label={reason ? reason : "Selected"} />
                    </Grid>
                }
                <Grid>
                    <Button onClick={() => openPDFDialog()}>Export To PDF</Button>
                </Grid>
                {stepIndex === 5 && (
                    <Grid>
                        <Button onClick={openParamDialog}>
                            Calculate Trip Rate For Survey
                        </Button>
                    </Grid>
                )}
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 0 88px', justifyContent: 'flex-end' }}>
                    <Tooltip title="Previous Site">
                        <IconButton disabled={!prevRef} onClick={() => navigate(getSiblingSiteUrl(location.search, prevRef), { replace: true })}>
                            <ArrowCircleLeftIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Next Site">
                        <IconButton disabled={!nextRef} onClick={() => navigate(getSiblingSiteUrl(location.search, nextRef), { replace: true })}>
                            <ArrowCircleRightIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Paper sx={{ flexGrow: 1, overflow: "hidden", mt: '8px', flexDirection: "column", display: "flex" }}>
                <SitePageOptions
                    value={stepIndex}
                    setValue={(value) => {
                        setIsEditing(false);
                        setStepIndex(value);
                    }}
                    hasParkingDetails={hasParkingDetails}
                    hasDevelopmentDetails={developmentDetails}
                    hasTravelPlan={hasTravelPlan}
                    isAdmin={isAdmin}
                    parkingSupported={parkingSupported}
                    isEditing={isEditing}
                    addDevelopment={addDevelopment}
                    addParking={addParking}
                    addTravelPlan={addTravelPlan}
                />
                <Box sx={{ flexGrow: 1, overflow: "auto", mt: '8px' }}>
                    {
                        siteRef &&
                        <CurrentStepComponent key={siteRef} siteFormContext={formContext} id={siteRef} surveyType={survType === 'MULTI-MODAL' ? 1 : 2} hasParkingDetails={hasParkingDetails} reloadSite={getSite} onSurveySelectionChange={handleSurveySelection} isDftConnectivityToolEnabled={isDftConnectivityToolEnabled} />
                    }
                </Box>
            </Paper>
            <DeselectionReasonDialog open={open !== null} onClose={() => setOpen(null)} references={[siteRef]} type={"site"} multiDeselection={false} />
            <ExportSitePDFDialog handleClose={closePDFDialog} dialogOpen={exportPDFDialog} exportPDF={getReportPDF} hasParking={hasParkingDetails ?? false} hasTravelPlan={hasTravelPlan ?? false} />
            {paramDialogOpen && < TripRateParametersDialog params={availableTripRateParameters} handleClose={closeParamDialog} dialogOpen={paramDialogOpen} runTripRate={runTripRateForSurvey} />}
            <Dialog open={addDevelopmentOpen} onClose={() => !loading && setAddDevelopmentOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add development</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="normal"
                        fullWidth
                        label="Development name"
                        value={newDevelopmentName}
                        onChange={(event) => setNewDevelopmentName(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDevelopmentOpen(false)} disabled={loading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmAddDevelopment}
                        disabled={!newDevelopmentName.trim() || loading}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
        </SiteEditContext.Provider>
    );
}
